/* eslint-disable @next/next/no-img-element */
import React, { useCallback, useEffect, useState } from 'react'
import Cookies from 'js-cookie'
import { useRouter } from 'next/router'
import dayjs from 'dayjs'
import { Box, Button as MuiButton, Divider, Skeleton, Typography } from '@mui/material'
import CommonApi from '@/pages/api'
import LoginForm, { type Props as LoginFormProps } from '@/src/components/login-form/LoginForm'
import { CurrentStats } from '@/pages/api/endpoints/MarketData'
import { NewLineArrowIcon } from '@/src/assets/icons'
import { useStore } from '@/src/types/store'
import { formatPrice } from '@/src/types/helper'
import { useTier } from '@/src/hooks/useTier'
import { sanitizeExternalUrl } from '@/src/utils/safeUrl'

// Display names for the Boston tier codes; marketing renames only touch this map.
const PLAN_LABELS: Record<string, string> = {
  essentials: 'Relait Essentials',
  intelligence: 'Relait Intelligence'
}

const PLAN_HEADINGS: Record<string, (businessName: string) => string> = {
  essentials: name => `Investor communications for ${name}`,
  intelligence: name => `Shareholder intelligence for ${name}`
}

const PLAN_LEADS: Record<string, (businessName: string) => string> = {
  essentials: name =>
    `This portal keeps ${name} investors informed automatically: price sensitive ASX announcements are emailed to investor lists as they are released, with live website widgets to match, powered by Relait Essentials.`,
  intelligence: name =>
    `This portal gives the ${name} team registry-connected shareholder analytics, capital event insights and daily market reporting, powered by Relait Intelligence.`
}

/**
 * Branded landing page served at the tenancy root for Essentials and
 * Intelligence plans (no public investor centre). Everything renders from
 * data the tenancy already has: theme colours and logos, business name and
 * code from /theme, share price from /market-changes, and the
 * corporate_home_url added for Project Boston.
 */
const LandingPage = () => {
  const router = useRouter()
  const {
    homeTheme,
    themeLogo,
    themeLoading,
    TextColorType,
    statsData,
    setStatsData,
    setStatsLoading,
    profile,
    setProfile,
    openToast
  } = useStore()
  const [loading, setLoading] = useState(false)

  // Copy is keyed on the tier code. An unresolved plan lands on 'relait_360',
  // which is absent from the maps below, so the copy degrades to neutral
  // wording rather than implying a tier the tenant may not be on.
  const { plan } = useTier()
  const businessName = homeTheme?.business_name || ''

  // Sanitised at the point of render, not trusted from the payload.
  //
  // corporate_home_url is tenant-controlled, is stored without server-side
  // scheme validation, and reaches this component through the unauthenticated
  // /theme endpoint. Interpolated straight into an href it is a stored-XSS
  // sink: a `javascript:` value would execute on this tenancy's public root.
  // The admin form's https check is client-side only and a direct PATCH
  // bypasses it, so this is the control that actually holds.
  //
  // primary_domain goes through the same gate — it is tenant data too, and a
  // malformed value would otherwise produce a broken or unsafe link.
  const websiteUrl =
    sanitizeExternalUrl(homeTheme?.corporate_home_url) ??
    sanitizeExternalUrl(homeTheme?.primary_domain ? `https://${homeTheme.primary_domain}` : null)

  // Same cache-aware fetch the IC header uses; /market-changes stays open
  // for every tier.
  const fetchCurrentStats = useCallback(async () => {
    if (statsData?.current_price) return
    setStatsLoading(true)
    try {
      const resp = await CommonApi({
        path: CurrentStats?.get({ interval: 'month' }),
        method: 'get'
      })
      if (resp.status) {
        setStatsData(resp.data)
      }
    } catch (err) {
      console.log('err', err)
    }
    setStatsLoading(false)
  }, [setStatsData, setStatsLoading, statsData?.current_price])

  useEffect(() => {
    fetchCurrentStats()
  }, [fetchCurrentStats])

  const openWebsite = () => {
    if (!websiteUrl) return
    const target = homeTheme?.home_link_behaviour === 'same_tab' ? '_self' : '_blank'
    // noopener: corporate_home_url is third-party content; never hand the
    // opened page a window.opener back-reference (reverse tabnabbing).
    window.open(websiteUrl, target, 'noopener,noreferrer')
  }

  const handleFormValues: LoginFormProps['onSubmit'] = async values => {
    // setProfile is a plain Zustand setter (set({ profile: data })), not a
    // React updater, so pass the next object rather than a callback.
    setProfile({ ...profile, user_type: '' })
    try {
      setLoading(true)
      const response = await CommonApi({
        path: 'login',
        method: 'post',
        data: { email: values?.email, password: values?.password }
      })
      if (response.status && response?.access_token) {
        openToast('Login successfully!', 'success')
        Cookies.set('access_token', response.access_token)
        router.push('/admin')
      } else if (response.status) {
        // Truthy status with no token: never persist the cookie (it would
        // store the literal string "undefined" and poison later requests).
        openToast('Login failed. Please try again.', 'error')
      }
    } catch (error: any) {
      // Unconfirmed and blocked accounts get their full flows (verification
      // modal, blocked page) on the login page; send those cases there.
      if (
        error?.response?.data?.msg === 'User is not confirmed.' ||
        error?.response?.data?.blocked
      ) {
        router.push('/login')
      }
      openToast(error?.response?.data?.msg || 'Invalid business user!', 'error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-[100dvh] bg-[#f5f5f8] flex flex-col">
      {/* Tenant-branded header bar, echoing the IC header conventions */}
      <div className="p-2 sm:p-4 pb-0">
        <div className="flex justify-between items-center h-[5.375rem] px-6 bg-primary-home rounded-[20px]">
          <div className="flex items-center">
            {themeLoading ? (
              <Skeleton className="h-10 w-[7rem]" />
            ) : (
              <img
                src={TextColorType?.primaryTextDark ? themeLogo?.dark_logo : themeLogo?.logo}
                alt="Logo"
                className="h-10 w-auto max-w-[12rem] object-contain fill-none"
              />
            )}
          </div>
          <div className="flex justify-right items-center gap-2 sm:gap-4">
            {statsData?.current_price !== 0 && statsData?.current_price !== undefined && (
              <div className="hidden sm:flex items-center gap-1 sm:gap-2">
                <Typography
                  variant="subtitle2"
                  className="text-[20px]"
                  color={TextColorType?.primaryTextDark ? '#fff' : '#000'}
                >
                  ASX:{homeTheme?.code}
                </Typography>
                <Typography
                  variant="subtitle2"
                  className="text-[20px]"
                  color={TextColorType?.primaryTextDark ? '#fff' : '#000'}
                >
                  ${formatPrice(statsData?.current_price)}
                </Typography>
                <Box className="flex items-center gap-1 bg-white py-0.2 px-1.5 rounded-[50px]">
                  {statsData?.percentage !== 0 && (
                    <NewLineArrowIcon
                      className={`w-[14px] h-[7px] ${
                        statsData?.percentage > 0 ? 'fill-[#2E7D32]' : 'fill-[#D32F2F] rotate-180'
                      }`}
                    />
                  )}
                  <Typography
                    variant="subtitle2"
                    className={`truncate ${statsData?.percentage === 0 ? 'text-black' : ''}`}
                    color={
                      statsData?.percentage === 0
                        ? '#000000'
                        : statsData?.percentage > 0
                          ? 'success.dark'
                          : 'error.dark'
                    }
                  >
                    {statsData?.percentage?.toFixed(2) || '00'}%
                  </Typography>
                </Box>
              </div>
            )}
            {websiteUrl && (
              <MuiButton
                variant="contained"
                size="large"
                onClick={openWebsite}
                className="bg-secondary-home rounded-full !px-3 sm:!px-5"
              >
                <Typography
                  variant="buttonLarge_md"
                  className="leading-[1.75rem] normal-case"
                  color={TextColorType?.secondaryTextDark ? '#ffffff' : '#000000'}
                >
                  Visit our website
                </Typography>
              </MuiButton>
            )}
          </div>
        </div>
      </div>

      {/* Hero + staff login */}
      <div className="flex-1 flex items-center justify-center px-4 py-10 sm:py-14">
        <div className="grid grid-cols-1 md:grid-cols-[1.1fr_0.9fr] gap-10 md:gap-14 max-w-[66.25rem] w-full items-center">
          <div>
            {themeLoading || !businessName ? (
              <Skeleton className="h-12 w-3/4" />
            ) : (
              <Typography
                variant="h1"
                // Tenant primary only when it is dark enough to read on the
                // light page background; light primaries fall back to text.
                className={`!text-[2.125rem] !leading-[1.2] !tracking-[-0.075rem] ${
                  TextColorType?.primaryTextDark ? 'text-primary-home' : 'text-[#000000DE]'
                }`}
              >
                {PLAN_HEADINGS[plan]?.(businessName) ??
                  `Investor portal for ${businessName}`}
              </Typography>
            )}
            <Typography variant="body1" color="text.secondary" className="pt-4 max-w-[30rem] !leading-[1.65]">
              {businessName ? PLAN_LEADS[plan]?.(businessName) ?? '' : ''}
            </Typography>
            {websiteUrl && (
              <div className="pt-5">
                <a
                  href={websiteUrl}
                  target={homeTheme?.home_link_behaviour === 'same_tab' ? '_self' : '_blank'}
                  rel="noopener noreferrer"
                  className="hover:underline"
                >
                  <Typography variant="buttonLink" className="text-secondary-home">
                    Looking for investor information? Visit our website &rarr;
                  </Typography>
                </a>
              </div>
            )}
            <div className="pt-7 flex items-center gap-2">
              <Typography variant="caption" color="text.secondary">
                Powered by Relait{PLAN_LABELS[plan] ? ` - ${PLAN_LABELS[plan]}` : ''}
              </Typography>
            </div>
          </div>

          <div className="bg-white rounded-[20px] shadow-[0_2px_6px_-1px_#0000001A,0_1px_6px_0_#0000000A,0_1px_8px_0_#00000005] px-8 sm:px-10 pt-10 pb-8">
            <Typography variant="h1" className="pb-6 text-center">
              Sign In
            </Typography>
            {/* The shared form sizes itself for the split login page; let it
                fill this card instead (style override only, no fork). */}
            <div className="[&_form]:!w-full [&_form]:!max-w-none">
              <LoginForm loading={loading} onSubmit={handleFormValues} isDisable={false} />
            </div>
            <Typography
              variant="caption"
              color="text.disabled"
              className="block pt-4 text-center !leading-[1.5]"
            >
              Access to this portal is for authorised {businessName || 'company'} staff. Contact
              your administrator if you need an account.
            </Typography>
          </div>
        </div>
      </div>

      {/* Footer, same links as the login page */}
      <div className="text-secondary-main w-full px-6 sm:px-8 pb-4 flex gap-4 items-center">
        <Typography variant="caption">© {dayjs().format('YYYY')} Relait PTY LTD</Typography>
        <div className="flex items-center gap-2">
          <Typography variant="buttonLarge_sm" color="text.secondary">
            <a
              href="https://www.relait.com.au/privacy-policy"
              target="_blank"
              className="hover:underline"
              rel="noreferrer"
            >
              Privacy
            </a>
          </Typography>
          <Divider orientation="vertical" className="h-3 w-px items-center flex justify-center" />
          <Typography variant="buttonLarge_sm" color="text.secondary">
            <a
              href="https://www.relait.com.au/eula"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline"
            >
              Terms
            </a>
          </Typography>
        </div>
      </div>
    </div>
  )
}

export default LandingPage
